
import SingleMachineMap from '@/components/Map/SingleMachineMap.vue';
import EditMissionSpeedDialog from '@/components/Mission/EditMissionSpeedDialog.vue';
import EditEngineSpeedDialog from '@/components/Mission/EditEngineSpeedDialog.vue';
import MqttMixIn from '@/mixins/mqttManagement';
import { WorkAreaStatus } from '@/models/ros-application/WorkAreaStatus';
import { EngineSpeedUserRequest } from '@/models/ui-application/EngineSpeedUserRequest';
import { MissionSpeedUserRequest } from '@/models/ui-application/MissionSpeedUserRequest';
import { MissionUserRequest } from '@/models/ui-application/MissionUserRequest';
import { UserRequest } from '@/models/ui-application/UserRequest';
import { PubSub } from '@/PubSubClass';
import router from '@/router';
import AlarmStore from '@/store/modules/alarms';
import GlobalStore from '@/store/modules/global';
import MachineStore from '@/store/modules/machine';
import MenuStore from '@/store/modules/menu';
import MissionStore from '@/store/modules/missions';
import { AlarmModel, AlarmSeverity } from '@/flightpath-components/models/AlarmModel';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import { RosMissionStatus } from '@/flightpath-components/models/enums/RosMissionStatus';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { ConvertMissionStatus, MissionStatusAutoTransitionMessage } from '@/flightpath-components/models/mqttPayload/MissionStatusAutoTransitionMessage';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Guid } from 'guid-typescript';
import { Container } from 'inversify';
import Component from 'vue-class-component';
import { Inject, Mixins, Watch } from 'vue-property-decorator';
import { getModule } from 'vuex-module-decorators';
import { Parameters } from '@/services/Parameters';
import { toNumber } from 'lodash';
import { DeployedSinglePathMissionCloudState } from '@/flightpath-components/models/enums/DeployedSinglePathMissionCloudState';

// Define the component in class-style
@Component({ name: 'MissionMonitoring', components: { SingleMachineMap, EditMissionSpeedDialog, EditEngineSpeedDialog } })
export default class Home extends Mixins(MqttMixIn) {
  public machineStore = getModule(MachineStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  public missionStore = getModule(MissionStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  public alarmStore = getModule(AlarmStore, this.$store);
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  @Inject('container')
  private container!: Container;
  confirmLoading = false;
  paramDialog = false;
  allowAutoConfirm = false;
  interval: number | undefined = undefined;
  remoteTimerInterval: number | undefined = undefined;
  steps: RosMissionStatus = RosMissionStatus.NoMissionLoaded;
  status: MissionStatusAutoTransitionMessage | null = null;
  currentJobId: string = Guid.createEmpty().toString();
  overlay = false;
  isCompleted = false;
  editSpeedDialog = false;
  editEngineSpeedDialog = false;
  panel = [0];
  windowSize = {
    x: 0,
    y: 0
  };
  mapHeightStyleA = 'calc(100% - 0px)';
  mapHeightStyleB = 'calc(100% - 80px)';
  stopMissionDialog = false;
  editFormValid = false;
  remoteTimer = 20; // 1 second increments
  snackBar = false;
  alertMessage = '';
  speed_kph = 0;
  speed_rpm = 0;
  launchRequestState = DeployedSinglePathMissionCloudState.LaunchRequest;
  pendingState = DeployedSinglePathMissionCloudState.Pending;

  showNotification(message: string) {
    this.snackBar = true;
    this.alertMessage = message;
  }
  editSpeed() {
    this.editSpeedDialog = true;
  }
  editEngineSpeed() {
    this.editEngineSpeedDialog = true;
  }
  get canResume() {
    return this.missionStore.deployedMissionPause && this.missionStore.missionStatusValid;
  }
  get timerPercent() {
    return 100 - (this.remoteTimer / 20) * 100;
  }
  get missionReady() {
    if (this.missionStore.missionStatus == null) return false;
    return this.missionStore.missionStatus?.RosMissionStatus?.mission_status >= 1 || this.isCompleted;
  }
  get showRemoteOverlay() {
    if (this.missionStore.missionStatus == null) return false;
    if (this.missionStore.deployedMission == null) return false;
    if (this.machineStore.machine == null) return false;
    if (this.missionStore.deployedMission.TargetMachine.machineId != this.machineStore.machine.machineId) return false;
    return this.missionStore.missionStatus?.RosMissionStatus?.mission_status == 2 && !this.missionStore.deployedMissionPause;
  }

  get showMonitorOverlay() {
    if (this.missionStore.missionStatus == null) return false;
    if (this.missionStore.deployedMission == null) return false;
    if (this.machineStore.machine == null) return false;
    if (this.missionStore.deployedMission.TargetMachine.machineId != this.machineStore.machine.machineId) return false;
    return this.missionStore.missionStatus?.RosMissionStatus?.mission_status != 2 || this.missionStore.deployedMissionPause || this.isCompleted;
  }

  get showPauseOverlay() {
    if (this.missionStore.missionStatus == null) return false;
    if (this.missionStore.deployedMission == null) return false;
    if (this.machineStore.machine == null) return false;
    if (this.missionStore.deployedMission.TargetMachine.machineId != this.machineStore.machine.machineId) return false;
    if (this.missionStore.missionComplete) return false;

    return this.missionStore.missionPercent > 0 && this.missionStore.deployedMissionPause;
  }

  get showStartOverlay() {
    if (this.missionStore.missionStatus == null) return false;
    if (this.missionStore.deployedMission == null) return false;
    if (this.machineStore.machine == null) return false;
    if (this.missionStore.deployedMission.TargetMachine.machineId != this.machineStore.machine.machineId) return false;
    if (this.missionStore.missionComplete) return false;

    return (
      (this.missionStore.missionStatus?.RosMissionStatus?.mission_status <= 1 ||
        (this.missionStore.deployedMissionPause && this.missionStore.missionStatus?.RosMissionStatus?.mission_status == 2)) &&
      this.missionStore.missionPercent == 0 &&
      this.missionStore.currentDeployedSinglePathMission != null
    );
  }

  get topAlarms() {
    const machine = this.machineStore.machine as MachineKey;
    const alarms = this.alarmStore.currentAlarms.filter((o) => o.Active && o.Severity == AlarmSeverity.Error && o.MachineId == machine.machineId);
    return alarms;
  }

  get implementType() {
    const implement = this.missionStore.applicationParameters.get('implement_type') ?? '';
    const key = implement != '' ? 'views.mission_monitoring.' + implement : '';
    return this.$te(key) ? this.$t(key) : '';
  }

  get engineSpeed() {
    return toNumber(this.missionStore.applicationParameters.get(Parameters.engineSpeed));
  }

  get missionProgress() {
    let missionProgress = this.missionStore.currentMissionProgress;
    let machineProgress = this.machineStore.missionProgress;

    if (machineProgress === null || machineProgress.waypoint_index === 0) {
      return missionProgress;
    } else return machineProgress;
  }
  async cancelDeployedMission() {
    try {
      this.overlay = false;
      if (this.missionStore.currentDeployedSinglePathMission != null) {
        await this.machineRequest(UserRequest.NoRequest);
        await this.flightPathService?.cancelDeployedSinglePathMission(
          this.missionStore.currentDeployedSinglePathMission as DeployedSinglePathMission,
          this.globalStore.accountId
        );
        this.missionStore.setDeployedMission(null);
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.paramDialog = false;
      router.back();
    }
  }

  async confirmSpeedUpdate(value: number) {
    // confirm and update requested max speed
    if (value <= 0 || value > 6) return;
    if (this.missionStore.deployedMission == null) return;

    try {
      await this.speedRequest(value);
      this.speed_kph = value;
    } catch (error) {
      console.log(error);
    } finally {
      this.editSpeedDialog = false;
    }
  }

  async confirmEngineSpeedUpdate(value: number) {
    // confirm and update requested max speed
    if (value <= 0 || value > 2800) return;
    if (this.missionStore.deployedMission == null) return;

    try {
      await this.engineSpeedRequest(value);
      this.speed_rpm = value;
    } catch (error) {
      console.log(error);
    } finally {
      this.editEngineSpeedDialog = false;
    }
  }

  async cancelMissionMonitoring() {
    try {
      this.overlay = false;
      if (this.missionStore.currentDeployedSinglePathMission != null) {
        this.missionStore.setDeployedMission(null);
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.paramDialog = false;
      router.back();
    }
  }

  async resume() {
    if (this.missionStore.deployedMissionPause) {
      this.confirmLoading = true;
      await this.machineRequest(UserRequest.MissionConfirmed);
      this.interval = window.setInterval(this.autoTimeout, 20000);
      //this.fetchMissionStatus();
    } else {
      //pause
      this.machineRequest(UserRequest.MissionPause);
    }
  }

  async pause() {
    await this.machineRequest(UserRequest.MissionPause);
  }
  async loadMission() {
    try {
      const mission = this.missionStore.currentDeployedSinglePathMission;
      if (!mission) {
        console.error('No mission selected for deployment');
        return;
      }
      this.confirmLoading = true;
      await this.flightPathService?.deploySinglePathMission(
        mission,
        this.globalStore.accountId
      );
      this.paramDialog = false;
      this.allowAutoConfirm = true;
      this.interval = window.setInterval(this.autoTimeout, 20000);
    } catch (error) {
      console.error('Error', error);
    }
  }
  async machineRequest(type: UserRequest) {
    const machine = this.machineStore.machine as MachineKey;
    const request: MissionUserRequest = {
      request_id: Guid.create().toString(),
      mission_deployment_id: this.missionStore.deployedMission?.MissionDeploymentID ?? Guid.createEmpty().toString(),
      user_request: type
    };

    await this.flightPathService?.setApplicationCloudCache({
      MachineKey: machine,
      TopicData: { 'ui-application/mission_user_request': JSON.stringify(request) }
    });
    if (type == UserRequest.MissionPause) {
      this.missionStore.setDeployedMissionPause(true);
    }
  }

  async speedRequest(value: number) {
    const machine = this.machineStore.machine as MachineKey;
    const request: MissionSpeedUserRequest = {
      request_id: Guid.create().toString(),
      mission_deployment_id: this.missionStore.deployedMission?.MissionDeploymentID as string,
      speed_kph: value
    };

    await this.flightPathService?.setApplicationCloudCache({
      MachineKey: machine,
      TopicData: { 'ui-application/mission_speed': JSON.stringify(request) }
    });
  }

  async engineSpeedRequest(value: number) {
    const machine = this.machineStore.machine as MachineKey;
    const request: EngineSpeedUserRequest = {
      request_id: Guid.create().toString(),
      mission_deployment_id: this.missionStore.deployedMission?.MissionDeploymentID as string,
      speed_rpm: value
    };

    await this.flightPathService?.setApplicationCloudCache({
      MachineKey: machine,
      TopicData: { 'ui-application/engine_speed': JSON.stringify(request) }
    });
  }

  async autoTimeout() {
    clearInterval(this.interval);
    await this.fetchMissionStatus();
    if (this.steps < RosMissionStatus.MissionRunnable) {
      this.machineRequest(UserRequest.MissionPause);
    }
  }
  async countDownRemoteTimer() {
    this.remoteTimer -= 1;
    if (this.remoteTimer <= 0) {
      clearInterval(this.remoteTimerInterval);
      this.machineRequest(UserRequest.MissionPause);
    } else if (this.steps == RosMissionStatus.MissionRunning) {
      clearInterval(this.remoteTimerInterval);
      this.remoteTimer = 20;
    }
  }

  async getDeployedMission() {
    if (this.flightPathService == null) return;
    const mission_id = this.missionStore.missionStatus?.RosMissionStatus.deployed_mission_id;
    if (mission_id == undefined || mission_id == Guid.EMPTY.toString()) return;
    const response = await this.flightPathService.getDeployedSinglePathMission(mission_id, this.globalStore.accountId);
    if (response != null) {
      const json = JSON.stringify(response.data);
      const mission = DeployedSinglePathMission.toModel(json);
      this.missionStore.setDeployedMission(mission);
    }
  }
  async updateSteps(status: MissionStatusAutoTransitionMessage | null) {
    try {
      if (status == null || status.RosMissionStatus == null) this.steps = RosMissionStatus.NoMissionLoaded;
      else this.steps = status.RosMissionStatus.mission_status;
      if (
        this.status != null &&
        status?.RosMissionStatus.deployed_mission_id == Guid.createEmpty().toString() &&
        this.status?.RosMissionStatus.deployed_mission_id != Guid.createEmpty().toString()
      ) {
        this.isCompleted = true;
      }

      if (this.isCompleted) return;

      if (
        status != null &&
        this.status != null &&
        status?.RosMissionStatus?.mission_status <= RosMissionStatus.MissionLoaded &&
        this.status?.RosMissionStatus?.mission_status >= RosMissionStatus.MissionRunning
      ) {
        await this.getDeployedMission();
      }

      this.missionStore.setMissionStatus(status);

      if (status?.FlightPathMissionStatus.job_id != Guid.createEmpty().toString()) {
        this.currentJobId = status?.FlightPathMissionStatus.job_id as string;
        this.missionStore.setActiveJob(this.currentJobId);
      }

      switch (this.steps) {
        case RosMissionStatus.NoMissionLoaded:
          if (!this.missionStore.deployedMissionPause && !this.missionStore.missionComplete) {
            this.confirmLoading = false;
            clearInterval(this.interval);
            clearInterval(this.remoteTimerInterval);
            this.remoteTimer = 20;
          }
          break;
        case RosMissionStatus.MissionLoaded:
          if (this.missionStore.missionComplete) break;
          if (this.missionStore.missionPercent > 0 && !this.missionStore.deployedMissionPause) {
            this.missionStore.setDeployedMissionPause(true);
          } else {
            this.confirmLoading = false;
            clearInterval(this.interval);
            clearInterval(this.remoteTimerInterval);
            this.remoteTimer = 20;

            if (this.missionStore.missionStatusValid && this.allowAutoConfirm) {
              this.interval = window.setInterval(this.autoTimeout, 20000);
              this.confirmLoading = true;
              this.missionStore.setDeployedMissionPause(false);
              this.machineRequest(UserRequest.MissionConfirmed);
              this.allowAutoConfirm = false;
            } else {
              this.missionStore.setDeployedMissionPause(true);
            }
          }
          break;
        case RosMissionStatus.MissionRunnable:
          clearInterval(this.interval);
          clearInterval(this.remoteTimerInterval);
          this.remoteTimer = 20;
          if (!this.missionStore.missionStatusValid && !this.missionStore.deployedMissionPause && !this.missionStore.missionComplete) {
            this.machineRequest(UserRequest.MissionPause);
            this.confirmLoading = false;
          } else {
            this.paramDialog = false;
            this.confirmLoading = false;
            this.missionStore.setDeployedMissionPause(false);
            this.remoteTimerInterval = window.setInterval(this.countDownRemoteTimer, 1000);
          }
          break;
        case RosMissionStatus.MissionRunning:
          this.confirmLoading = false;
          this.paramDialog = false;
          clearInterval(this.interval);
          clearInterval(this.remoteTimerInterval);
          if (this.missionStore.deployedMissionPause && this.isStateChanged(this.lastMissionStatus)) this.missionStore.setDeployedMissionPause(false);
          this.remoteTimer = 20;

          break;
        case RosMissionStatus.MissionCompleted:
          if (this.isStateChanged(this.lastMissionStatus)) await this.machineRequest(UserRequest.NoRequest);
          this.missionStore.setDeployedMissionPause(true);

          break;
        default:
          break;
      }
    } catch (error) {
      console.log(error);
      this.confirmLoading = false;
      clearInterval(this.interval);
      clearInterval(this.remoteTimerInterval);
      this.remoteTimer = 20;
    }
  }

  async created(): Promise<void> {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
    this.machineStore = getModule(MachineStore, this.$store);
    this.missionStore.setMissionStatus(null);
    this.missionStore.setDeployedMissionPause(false);
    clearInterval(this.interval);
    clearInterval(this.remoteTimerInterval);
    this.remoteTimer = 20;

    this.addMqttVisibility(this.globalStore.missionStatusTopic, () =>
      PubSub.subscribe(this.globalStore.missionStatusTopic).subscribe({
        next: (data) => this.updateRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }
  async mounted() {
    this.isCompleted = false;
    this.onResize();

    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });
    this.missionStore.setActiveJob(Guid.createEmpty().toString());
    await this.fetchMissionStatus();
    if (this.missionStore.deployedMission == null) await this.getDeployedMission();
    this.setInitialSpeed();
    this.speed_rpm = toNumber(this.missionStore.applicationParameters.get(Parameters.engineSpeed) ?? 0);

    if (
      this.missionStore.missionStatus != null &&
      !this.missionStore.missionComplete &&
      !this.missionStore.deployedMissionPause &&
      (this.missionStore.missionStatus.RosMissionStatus == null ||
        this.missionStore.missionStatus.RosMissionStatus.mission_status < RosMissionStatus.MissionLoaded)
    ) {
      this.paramDialog = true;
    }
    this.menuStore.setDrawer(false);
  }

  @Watch('machineStore.exxactWorkAreaStatus')
  checkWorkAreaStatus(newValue: WorkAreaStatus | null, oldValue: WorkAreaStatus | null) {
    if (oldValue != null && newValue != null && oldValue.inside_geofence != newValue.inside_geofence) {
      const rowMessage = newValue.inside_geofence ? this.$t('views.mission_monitoring.entering_row') : this.$t('views.mission_monitoring.leaving_row');

      this.showNotification(`Machine ${this.machineStore.machineName} : ${rowMessage}`);
    }
  }
  private setInitialSpeed() {
    this.speed_kph = this.missionStore.deployedMission?.TargetSpeedKph ?? 0;
    if (this.machineStore.speedStatus == null || this.machineStore.speedStatus.mission_deployment_id != this.missionStore.deployedMission?.MissionDeploymentID)
      return;
    this.speed_kph = this.machineStore.speedStatus.speed_kph ?? 0;
  }

  destroyed() {
    this.removeMqttVisibility();
    clearInterval(this.interval);
    clearInterval(this.remoteTimerInterval);
  }

  private async fetchMissionStatus() {
    if (this.flightPathService != null) {
      const machineId = this.machineStore.machine?.machineId.toString();
      const response = await this.flightPathService.getMissionStatus(machineId as string);
      if (response != null) {
        const json = JSON.stringify(response.data);
        const status = ConvertMissionStatus.stringToMissionStatusModel(json);

        if (this.status == null || new Date(status.SentUTC).getTime() >= new Date(this.status.SentUTC).getTime()) {
          this.updateSteps(status);
          this.status = status;
        }
      }
    }
  }

  async returnToHome() {
    try {
      if (this.missionStore.currentDeployedSinglePathMission != null && this.status?.RosMissionStatus.deployed_mission_id != Guid.createEmpty().toString()) {
        await this.flightPathService?.cancelDeployedSinglePathMission(
          this.missionStore.currentDeployedSinglePathMission as DeployedSinglePathMission,
          this.globalStore.accountId
        );
        this.missionStore.setDeployedMission(null);
      }
    } catch (error) {
      console.log(error);
    } finally {
      router.back();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private updateRecieved(data: any): void {
    const response = data.value as string;
    const json = JSON.stringify(response);
    //console.info('Mission Status Event', json);
    const status = ConvertMissionStatus.stringToMissionStatusModel(json);
    if (status.Key.machineId != this.machineStore.machine?.machineId) return;
    this.fetchMissionStatus();
  }

  containerWidth() {
    return this.menuStore.drawer ? this.windowSize.x - 380 : this.windowSize.x - 140;
  }

  getMapHeight() {
    return this.showMonitorOverlay ? this.mapHeightStyleB : this.mapHeightStyleA;
  }

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  getSnackbarWidth() {
    return this.windowSize.x - 30;
  }

  getTranslatedString(basePath: string, req: string) {
    return this.$t(basePath + req);
  }

  isStateChanged(status: RosMissionStatus | undefined) {
    return this.steps != status;
  }

  get targetSpeed() {
    return this.speed_kph ?? 0;
  }

  get targetEngineSpeed() {
    return this.speed_rpm ?? 0;
  }

  get lastMissionStatus(): RosMissionStatus {
    return this.status?.RosMissionStatus.mission_status ?? RosMissionStatus.NoMissionLoaded;
  }

  getTranslatedAlarmDescription(alarm: AlarmModel) {
    return this.$t(`alarms.description.` + this.getAlarmKey(alarm));
  }

  getTranslatedAlarmAction(alarm: AlarmModel) {
    return this.$t(`alarms.action.` + this.getAlarmKey(alarm));
  }

  getAlarmKey(alarm: AlarmModel) {
    return alarm.Package + '_' + alarm.NodeName + '_' + alarm.AlarmId;
  }
}
